import * as React from "react";
import {} from "@blueprintjs/core";
import { useCommonHooks } from "../../hooks";
import { ActionCode, MentionEditDto } from "../../api";
import { useCrudApi } from "../../hooks/useCrudApi";

import {
  FGCheckboxInput,
  FGTextInput,
  FlexFieldGroup,
  showSuccess,
  useFGFormik,
} from "nsitools-react";
import * as Yup from "yup";
import { useLanguages } from "../../hooks/applicationHooks/useLanguages";
import {
  CardFieldGroup,
  CustomFormGenerator,
  FGTranslations,
} from "../../components";

export interface MentionDetailPanelProps {
  mentionId: number;
}

export const MentionDetailPanel: React.FunctionComponent<
  MentionDetailPanelProps
> = ({ mentionId }) => {
  const { apis, t, hasAction, validators } = useCommonHooks();
  const { buildDefaultDataTranslations } = useLanguages();

  const isEditable = React.useMemo(
    () => hasAction([ActionCode.EditMention]),
    [hasAction]
  );

  const { data, isFetching, validationErrors, onSave, isSaving, refetch } =
    useCrudApi({
      queryKey: ["mention-details", mentionId],
      fetchFn: async () => {
        if (mentionId > 0) {
          return apis.mentions.get(mentionId);
        }
        const newMention = MentionEditDto.fromJS({});
        newMention.dataTranslation = await buildDefaultDataTranslations();
        newMention.isActive = true;

        return newMention;
      },
      saveFn: async (toSave: MentionEditDto) => {
        return apis.mentions.save(toSave);
      },
      onSaved: () => {
        showSuccess(t("MentionSaved"));
        refetch();
      },
      serverValidationRootKey: "Dto",
    });

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        code: Yup.string().required(t("Required")).max(50),
        dataTranslation: validators.dataTranslationValidator,
      }),
    [t, validators.dataTranslationValidator]
  );

  const formik = useFGFormik({
    initialValues: data,
    onSubmit: (d) => onSave(d),
    validationSchema: validationSchema,
  });

  return (
    <FlexFieldGroup columns={2}>
      <CardFieldGroup title={t("Details")}>
        <CustomFormGenerator
          initialValues={data}
          loading={isFetching}
          onSubmit={onSave}
          saving={isSaving}
          editable={isEditable}
          editMode={isEditable}
          showDeleteButton={false}
          validationErrors={validationErrors}
          validationSchema={validationSchema}
          formik={formik}
        >
          <FGTextInput name="code" label={t("Code")} forceCase="upper" />
          <FGTranslations label={t("Name")} />
          <FGCheckboxInput name="isActive" label={t("IsActive")} />
        </CustomFormGenerator>
      </CardFieldGroup>
    </FlexFieldGroup>
  );
};

export default MentionDetailPanel;
